/**
 * Product gallery: unified horizontal scroll-snap rail (all breakpoints), thumbnails, variant sync.
 * Desktop: thumb strip max-height (left) / max-width (below) track the main viewport via CSS variables.
 * Below 1024px: peek rail inside .page-width; centered scroll-snap; JS scroll matches slide centers.
 * Fine pointers: horizontal drag to scroll (threshold + horizontal intent so video controls still work); touch uses native overflow scrolling. Embed slides use edge gutters (iframe events do not bubble).
 */
(function () {
  'use strict';

  function qs(root, sel) {
    return (root || document).querySelector(sel);
  }

  function qsa(root, sel) {
    return Array.prototype.slice.call((root || document).querySelectorAll(sel));
  }

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function scrollBehavior() {
    return prefersReducedMotion() ? 'auto' : 'smooth';
  }

  function parseDataIndex(el, name) {
    var v = parseInt(el.getAttribute(name), 10);
    return isNaN(v) ? null : v;
  }

  function isInteractiveTarget(el) {
    if (!el || !el.closest) return false;
    /* Native <video>: allow pointerdown so horizontal drag can scroll; taps still reach the video after a short move threshold. Omit video here. Iframe embeds never bubble — use edge gutters in markup. */
    return !!el.closest(
      'button, a, input, textarea, select, iframe, [data-gallery-thumb]'
    );
  }

  var DRAG_THRESHOLD_PX = 10;
  /* Keep in sync with sections.css @media (min-width: 1024px) gallery layout. */
  var MQ_DESKTOP_GALLERY = '(min-width: 1024px)';

  function isDesktopGalleryLayout() {
    return window.matchMedia(MQ_DESKTOP_GALLERY).matches;
  }

  /** Peek mode uses scroll-padding 0 on .product-gallery__track; center = midpoint of visible width. */
  function peekSnapCenterX(track) {
    return track.scrollLeft + track.clientWidth / 2;
  }

  function scrollLeftToCenterSlide(track, slide) {
    var slideMid = slide.offsetLeft + slide.offsetWidth / 2;
    return slideMid - track.clientWidth / 2;
  }

  function scrollTrackToIndex(track, slides, index, behavior) {
    var el = slides[index];
    if (!el) return;
    var beh = behavior || scrollBehavior();
    if (isDesktopGalleryLayout()) {
      var w = track.clientWidth || 0;
      if (!w) return;
      track.scrollTo({
        left: index * w,
        behavior: beh,
      });
      return;
    }
    var left = scrollLeftToCenterSlide(track, el);
    var maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);
    left = Math.max(0, Math.min(left, maxScroll));
    track.scrollTo({
      left: left,
      behavior: beh,
    });
  }

  function indexNearestScrollPosition(track, slides, count) {
    if (isDesktopGalleryLayout()) {
      var w = track.clientWidth || 0;
      if (!w) return 0;
      return Math.max(0, Math.min(count - 1, Math.round(track.scrollLeft / w)));
    }
    var cx = peekSnapCenterX(track);
    var best = 0;
    var bestDist = Infinity;
    for (var i = 0; i < count; i++) {
      var slideEl = slides[i];
      var mid = slideEl.offsetLeft + slideEl.offsetWidth / 2;
      var dist = Math.abs(mid - cx);
      if (dist < bestDist) {
        bestDist = dist;
        best = i;
      }
    }
    return best;
  }

  function initGallery(root) {
    if (!root || root.getAttribute('data-product-gallery-init') === 'true') return;
    root.setAttribute('data-product-gallery-init', 'true');

    var track = qs(root, '[data-gallery-main-track]');
    var viewport = qs(root, '[data-gallery-viewport]');
    var slides = track ? qsa(track, '[data-gallery-slide]') : [];
    var thumbs = qsa(root, '[data-gallery-thumb]');
    // Per-image captions (only present when the product has Studio's
    // custom.image_captions and the section shows them). Keyed by slide
    // index; slides without a caption simply have no entry.
    var captionStrip = qs(root, '[data-gallery-captions]');
    var captions = qsa(root, '[data-gallery-caption]');
    var prevBtn = qs(root, '[data-gallery-prev]');
    var nextBtn = qs(root, '[data-gallery-next]');

    var count = slides.length;
    if (count === 0 || !track) return;

    var current = 0;
    var scrollRaf = null;
    var dragPointerDown = false;
    var dragCommitted = false;
    var dragPointerId = null;
    var dragStartX = 0;
    var dragStartY = 0;
    var dragStartScroll = 0;
    var skipNextScrollSync = false;
    var thumbBoundsObserver = null;
    var captionObserver = null;

    function syncThumbListBounds() {
      if (!viewport) return;
      var mq = window.matchMedia(MQ_DESKTOP_GALLERY);
      if (!mq.matches) {
        root.style.removeProperty('--product-gallery-thumbs-max-height');
        root.style.removeProperty('--product-gallery-thumbs-max-width');
        return;
      }
      var h = viewport.offsetHeight;
      var w = viewport.offsetWidth;
      if (h < 1 || w < 1) return;
      if (root.classList.contains('product-gallery--thumbs-below')) {
        root.style.setProperty('--product-gallery-thumbs-max-width', w + 'px');
        root.style.removeProperty('--product-gallery-thumbs-max-height');
      } else {
        root.style.setProperty('--product-gallery-thumbs-max-height', h + 'px');
        root.style.removeProperty('--product-gallery-thumbs-max-width');
      }
    }

    function setAriaCurrent(index) {
      thumbs.forEach(function (t, i) {
        t.setAttribute('aria-current', i === index ? 'true' : 'false');
      });
    }

    function setCaptionExpanded(cap, open) {
      var btn = cap.querySelector('[data-gallery-caption-toggle]');
      cap.classList.toggle('is-expanded', open);
      if (btn) btn.setAttribute('aria-expanded', open ? 'true' : 'false');
    }

    function syncCaptions(index) {
      captions.forEach(function (cap) {
        var on = parseDataIndex(cap, 'data-gallery-index') === index;
        cap.classList.toggle('is-active', on);
        // A new slide starts collapsed (one line, same height as every other).
        if (!on && cap.classList.contains('is-expanded')) setCaptionExpanded(cap, false);
      });
    }

    /* "More" appears only when there is more to read: an "About this photo"
       summary, or a caption the one-line clamp cuts at the current width.
       The button sits on the caption's own line, so showing it never
       changes the strip's height. Measured with the button hidden so its
       own width doesn't count as a cut. */
    function syncCaptionToggles() {
      captions.forEach(function (cap) {
        var btn = cap.querySelector('[data-gallery-caption-toggle]');
        if (!btn || cap.classList.contains('is-expanded')) return;
        if (cap.hasAttribute('data-gallery-caption-summary')) {
          btn.hidden = false;
          return;
        }
        var text = cap.querySelector('[data-gallery-caption-text]');
        btn.hidden = true;
        btn.hidden = !(text && text.scrollWidth > text.clientWidth + 1);
      });
    }

    function syncSlides(index) {
      syncCaptions(index);
      slides.forEach(function (slide, i) {
        var on = i === index;
        slide.classList.toggle('is-active', on);
        slide.setAttribute('aria-hidden', on ? 'false' : 'true');
        // inert pairs with aria-hidden so focusable descendants (inline-video
        // play/mute buttons on video slides) are pulled out of the tab order
        // while the slide is off-screen. Without this, axe flags aria-hidden-focus.
        if (on) {
          slide.removeAttribute('inert');
        } else {
          slide.setAttribute('inert', '');
        }
      });
    }

    function applyIndex(index, scrollToo) {
      if (index < 0 || index >= count) return;
      current = index;
      syncSlides(index);
      setAriaCurrent(index);
      if (scrollToo) {
        skipNextScrollSync = true;
        scrollTrackToIndex(track, slides, index, scrollBehavior());
      }
    }

    /* Prev/next navigation with wrap-around. Going past the boundary
       loops to the opposite end (Instagram/Apple-style infinite feel)
       — better UX than disabling the buttons at boundaries since the
       customer never has to second-guess whether the button works.
       The wrap case uses an instant scroll jump to avoid a long
       smooth-scroll all the way back across the rail; within-rail
       navigation stays smooth. */
    function navigateRelative(delta) {
      if (count <= 1) return;
      var nextIdx = (current + delta + count) % count;
      var wrapped =
        (delta > 0 && nextIdx === 0) ||
        (delta < 0 && nextIdx === count - 1);
      applyIndex(nextIdx, false);
      skipNextScrollSync = true;
      scrollTrackToIndex(
        track,
        slides,
        nextIdx,
        wrapped ? 'auto' : scrollBehavior()
      );
    }

    function showSlide(index) {
      applyIndex(index, true);
    }

    /* M4 — variant-image swap as a 180ms opacity crossfade instead of a
       scroll across the rail. A decorative clone of the CURRENT image
       (same already-loaded currentSrc → no new request) is laid over it,
       the rail jumps instantly to the new slide underneath, and the clone
       fades out. Never touches the real images' opacity, never runs before
       the page has loaded (the first image is never delayed or faded), and
       falls back to the plain showSlide under reduced motion, for non-image
       slides, or when the current image hasn't loaded. */
    function crossfadeToSlide(index) {
      var host = viewport ? viewport.parentElement : null;
      var fromSlide = slides[current];
      var toSlide = slides[index];
      var fromImg = fromSlide && fromSlide.querySelector('img');
      if (
        index === current ||
        !host ||
        !toSlide ||
        !fromImg ||
        !fromImg.complete ||
        !fromImg.currentSrc ||
        document.readyState !== 'complete' ||
        prefersReducedMotion() ||
        fromSlide.getAttribute('data-media-type') !== 'image' ||
        toSlide.getAttribute('data-media-type') !== 'image'
      ) {
        showSlide(index);
        return;
      }
      var hostRect = host.getBoundingClientRect();
      var r = fromImg.getBoundingClientRect();
      var cs = window.getComputedStyle(fromImg);
      var ghost = document.createElement('img');
      ghost.src = fromImg.currentSrc;
      ghost.alt = '';
      ghost.setAttribute('aria-hidden', 'true');
      ghost.className = 'product-gallery__crossfade';
      ghost.style.top = r.top - hostRect.top + 'px';
      ghost.style.left = r.left - hostRect.left + 'px';
      ghost.style.width = r.width + 'px';
      ghost.style.height = r.height + 'px';
      ghost.style.objectFit = cs.objectFit;
      ghost.style.objectPosition = cs.objectPosition;
      /* Rounded media: borrow the first non-zero radius from the image or the
         wrappers that clip it, so the overlay's corners match. */
      var clipEls = [fromImg, fromImg.parentElement, fromSlide];
      for (var c = 0; c < clipEls.length; c++) {
        var rad = clipEls[c] ? window.getComputedStyle(clipEls[c]).borderRadius : '';
        if (rad && rad !== '0px') {
          ghost.style.borderRadius = rad;
          break;
        }
      }
      host.appendChild(ghost);

      applyIndex(index, false);
      skipNextScrollSync = true;
      scrollTrackToIndex(track, slides, index, 'auto');

      var removed = false;
      function removeGhost() {
        if (removed) return;
        removed = true;
        if (ghost.parentNode) ghost.parentNode.removeChild(ghost);
      }
      window.requestAnimationFrame(function () {
        window.requestAnimationFrame(function () {
          ghost.classList.add('is-fading');
        });
      });
      ghost.addEventListener('transitionend', removeGhost);
      window.setTimeout(removeGhost, 400);
    }

    function snapToNearest() {
      var idx = indexNearestScrollPosition(track, slides, count);
      skipNextScrollSync = true;
      scrollTrackToIndex(track, slides, idx, scrollBehavior());
      applyIndex(idx, false);
    }

    thumbs.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var idx = parseDataIndex(btn, 'data-gallery-index');
        if (idx !== null) showSlide(idx);
      });
    });

    track.addEventListener(
      'scroll',
      function () {
        if (skipNextScrollSync) {
          skipNextScrollSync = false;
          return;
        }
        if (scrollRaf !== null) return;
        scrollRaf = window.requestAnimationFrame(function () {
          scrollRaf = null;
          var idx = indexNearestScrollPosition(track, slides, count);
          if (idx === current) return;
          current = idx;
          syncSlides(idx);
          setAriaCurrent(idx);
        });
      },
      { passive: true }
    );

    /* Prev / next click — wrap-around via navigateRelative; buttons
       stay visible at the boundaries (no [disabled] state). */
    if (prevBtn) {
      prevBtn.addEventListener('click', function () {
        navigateRelative(-1);
      });
    }
    if (nextBtn) {
      nextBtn.addEventListener('click', function () {
        navigateRelative(1);
      });
    }

    if (viewport && count > 1) {
      viewport.addEventListener('keydown', function (ev) {
        if (ev.key !== 'ArrowLeft' && ev.key !== 'ArrowRight') return;
        ev.preventDefault();
        navigateRelative(ev.key === 'ArrowRight' ? 1 : -1);
      });
    }

    function bindPointerDrag() {
      var mqFine = window.matchMedia('(pointer: fine)');
      if (!mqFine.matches) return;

      track.addEventListener(
        'dragstart',
        function (ev) {
          ev.preventDefault();
        },
        true
      );

      track.addEventListener('pointerdown', function (ev) {
        if (ev.pointerType !== 'mouse' && ev.pointerType !== 'pen') return;
        if (ev.button !== 0) return;
        if (isInteractiveTarget(ev.target)) return;

        dragPointerDown = true;
        dragCommitted = false;
        dragPointerId = ev.pointerId;
        dragStartX = ev.clientX;
        dragStartY = ev.clientY;
        dragStartScroll = track.scrollLeft;
      });

      track.addEventListener('pointermove', function (ev) {
        if (!dragPointerDown || ev.pointerId !== dragPointerId) return;
        var dx = ev.clientX - dragStartX;
        var dy = ev.clientY - dragStartY;

        if (!dragCommitted) {
          if (Math.abs(dx) < DRAG_THRESHOLD_PX) return;
          /* Prefer vertical gestures for in-video controls (scrub/volume). */
          if (Math.abs(dx) <= Math.abs(dy)) return;
          dragCommitted = true;
          track.classList.add('is-dragging');
          track.style.scrollSnapType = 'none';
          try {
            track.setPointerCapture(ev.pointerId);
          } catch (e) {
            /* ignore */
          }
        }

        track.scrollLeft = dragStartScroll - dx;
      });

      function endPointerGesture(ev) {
        if (!dragPointerDown) return;
        if (ev && ev.pointerId !== dragPointerId) return;
        dragPointerDown = false;
        var wasCommitted = dragCommitted;
        dragCommitted = false;
        var pid = dragPointerId;
        dragPointerId = null;
        track.classList.remove('is-dragging');
        track.style.scrollSnapType = '';
        try {
          if (pid != null && track.hasPointerCapture(pid)) {
            track.releasePointerCapture(pid);
          }
        } catch (e) {
          /* ignore */
        }
        if (wasCommitted) {
          snapToNearest();
        }
      }

      track.addEventListener('pointerup', endPointerGesture);
      track.addEventListener('pointercancel', endPointerGesture);
      track.addEventListener('lostpointercapture', function (ev) {
        if (!dragPointerDown && !dragCommitted) return;
        if (ev.pointerId !== dragPointerId) return;
        dragPointerDown = false;
        var wasCommitted = dragCommitted;
        dragCommitted = false;
        dragPointerId = null;
        track.classList.remove('is-dragging');
        track.style.scrollSnapType = '';
        if (wasCommitted) {
          snapToNearest();
        }
      });
    }

    bindPointerDrag();

    function bindProductGalleryScrollbarRail() {
      var thumb = root.querySelector('[data-gallery-scrollbar-thumb]');
      var barTrack = root.querySelector('[data-gallery-scrollbar-track]');
      if (count < 2 || !thumb || !barTrack) return;

      function maxScrollBar() {
        return Math.max(0, track.scrollWidth - track.clientWidth);
      }

      function updateScrollbarThumb() {
        var ms = maxScrollBar();
        var cw = track.clientWidth;
        var sw = track.scrollWidth;
        if (ms < 1 || sw <= cw) {
          thumb.style.width = '100%';
          thumb.style.left = '0%';
          thumb.classList.add('is-full');
          return;
        }
        thumb.classList.remove('is-full');
        var thumbPct = Math.min(100, Math.max(18, (cw / sw) * 100));
        var travelPct = 100 - thumbPct;
        var leftPct = travelPct > 0 ? (track.scrollLeft / ms) * travelPct : 0;
        thumb.style.width = thumbPct + '%';
        thumb.style.left = leftPct + '%';
      }

      track.addEventListener(
        'scroll',
        function () {
          updateScrollbarThumb();
        },
        { passive: true }
      );

      if (typeof ResizeObserver !== 'undefined') {
        var roBar = new ResizeObserver(updateScrollbarThumb);
        roBar.observe(track);
      } else {
        window.addEventListener('resize', updateScrollbarThumb, { passive: true });
      }

      updateScrollbarThumb();

      barTrack.addEventListener('click', function (e) {
        if (e.target !== barTrack) return;
        var ms = maxScrollBar();
        if (ms < 1) return;
        var rect = barTrack.getBoundingClientRect();
        var x = e.clientX - rect.left;
        var ratio = x / rect.width;
        var target = ratio * ms;
        track.scrollTo({
          left: target,
          behavior: scrollBehavior(),
        });
      });

      thumb.addEventListener('pointerdown', function (e) {
        if (e.button !== 0) return;
        e.preventDefault();
        var startX = e.clientX;
        var startScroll = track.scrollLeft;
        function move(ev) {
          var ms2 = maxScrollBar();
          if (ms2 < 1) return;
          var trackW = barTrack.clientWidth;
          var thumbW = thumb.offsetWidth;
          var travel = Math.max(1, trackW - thumbW);
          var dx = ev.clientX - startX;
          var next = startScroll + (dx / travel) * ms2;
          track.scrollLeft = Math.max(0, Math.min(ms2, next));
        }
        function up() {
          document.removeEventListener('pointermove', move);
          document.removeEventListener('pointerup', up);
        }
        document.addEventListener('pointermove', move);
        document.addEventListener('pointerup', up);
      });
    }

    bindProductGalleryScrollbarRail();

    function scheduleThumbBoundsSync() {
      window.requestAnimationFrame(function () {
        syncThumbListBounds();
      });
    }

    scheduleThumbBoundsSync();
    if (typeof ResizeObserver !== 'undefined') {
      thumbBoundsObserver = new ResizeObserver(function () {
        scheduleThumbBoundsSync();
      });
      thumbBoundsObserver.observe(viewport);
    }

    if (captionStrip && captions.length) {
      captionStrip.addEventListener('click', function (e) {
        var btn = e.target.closest ? e.target.closest('[data-gallery-caption-toggle]') : null;
        var cap = btn ? btn.closest('[data-gallery-caption]') : null;
        if (!cap) return;
        setCaptionExpanded(cap, btn.getAttribute('aria-expanded') !== 'true');
      });
      syncCaptionToggles();
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(syncCaptionToggles);
      if (typeof ResizeObserver !== 'undefined') {
        captionObserver = new ResizeObserver(syncCaptionToggles);
        captionObserver.observe(captionStrip);
      }
    }

    function onResize() {
      skipNextScrollSync = true;
      scrollTrackToIndex(track, slides, current, 'auto');
      scheduleThumbBoundsSync();
    }

    function onVariantChange(ev) {
      if (!root.isConnected) {
        document.removeEventListener('theme:product:variant-change', onVariantChange);
        window.removeEventListener('resize', onResize);
        return;
      }
      var d = ev.detail;
      if (!d || !d.variant) return;
      var fm = d.variant.featured_media;
      var fid = fm && fm.id ? fm.id : null;
      if (!fid && d.variant.featured_image) {
        fid = d.variant.featured_image.id || null;
      }
      if (!fid) return;
      slides.forEach(function (slide, i) {
        var mid = slide.getAttribute('data-media-id');
        if (mid && String(mid) === String(fid)) {
          crossfadeToSlide(i);
        }
      });
    }

    window.addEventListener('resize', onResize, { passive: true });
    document.addEventListener('theme:product:variant-change', onVariantChange);

    root.showSlide = showSlide;
    root._serenGalleryTeardown = function () {
      window.removeEventListener('resize', onResize);
      document.removeEventListener('theme:product:variant-change', onVariantChange);
      if (thumbBoundsObserver) thumbBoundsObserver.disconnect();
      if (captionObserver) captionObserver.disconnect();
    };
  }

  function initAll() {
    document.querySelectorAll('[data-product-gallery]').forEach(initGallery);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (ev) {
    if (!ev.target) return;
    ev.target.querySelectorAll('[data-product-gallery]').forEach(initGallery);
  });

  document.addEventListener('shopify:section:unload', function (ev) {
    if (!ev.target) return;
    ev.target.querySelectorAll('[data-product-gallery]').forEach(function (root) {
      if (typeof root._serenGalleryTeardown === 'function') {
        root._serenGalleryTeardown();
        root._serenGalleryTeardown = null;
        root.removeAttribute('data-product-gallery-init');
      }
    });
  });
})();
